import { Lock } from 'lucide-react';
import { Link, Outlet } from 'react-router';

import { Container } from '@/components/ui/Container';
import { useEntitlements } from '@/features/billing/hooks/useEntitlements';
import { RequireFlag } from '@/features/system/components/RequireFlag';
import { useT } from '@/lib/i18n/context';

/**
 * Route guard for an area the merchant's plan does not include.
 *
 * Convenience and honesty, not security: the API refuses the writes anyway.
 * This stops the page rendering tools that would only end in a 402, and says
 * where the feature lives instead. The sidebar hides the same items by the
 * same answer, so a link and its destination never disagree.
 *
 * `flag` names the platform kill switch that covers the same area. The plan
 * wall is checked first: a merchant whose plan lacks the feature is told to
 * upgrade, not to wait for an incident that would not help them anyway.
 */
export function RequireEntitlement({
  feature,
  label,
  flag,
}: {
  feature: string;
  label: string;
  flag?: string;
}) {
  const { granted } = useEntitlements();
  const t = useT();

  if (granted(feature)) return flag ? <RequireFlag flag={flag} label={label} /> : <Outlet />;

  return (
    <Container className="py-20 text-center">
      <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-brand-50">
        <Lock className="size-5 text-brand-600" aria-hidden />
      </span>
      <h1 className="mt-4 text-2xl font-bold text-ink">{t('billing.guard.title', { feature: label })}</h1>
      <p className="mx-auto mt-2 max-w-md text-muted">{t('billing.guard.description')}</p>
      <Link
        to="/app/billing"
        className="mt-6 inline-flex h-11 items-center justify-center rounded-xl bg-brand-500 px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
      >
        {t('billing.guard.see_plans')}
      </Link>
    </Container>
  );
}
